import HomeNavbar from "./HomeNavbar";
import { motion } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import CompanyLogo from "../../../assets/images/un_resume_logo2.png";
import { MapPinIcon, CalendarIcon, SearchIcon } from "lucide-react";

const timeAgo = (iso) => {
  if (!iso) return "";
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "Posted today";
  if (days === 1) return "Posted yesterday";
  if (days < 7) return `Posted ${days} days ago`;
  const weeks = Math.floor(days / 7);
  return weeks === 1 ? "Posted a week ago" : `Posted ${weeks} weeks ago`;
};

export default function Volunteer() {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    let active = true;
    axios
      .get("/api/volunteer-opportunities/", { withCredentials: true })
      .then((res) => {
        if (!active) return undefined;
        const data = res.data?.results ?? res.data ?? [];
        setOpportunities(Array.isArray(data) ? data : []);
        return undefined;
      })
      .catch(() => undefined)
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, []);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return opportunities;
    return opportunities.filter((o) =>
      [o.title, o.company_name, o.description]
        .filter(Boolean)
        .some((v) => v.toLowerCase().includes(q)),
    );
  }, [opportunities, searchQuery]);

  return (
    <>
      <HomeNavbar />
      <div className="mt-16 p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        <div className="p-4 mt-6 mb-8">
          <h1 className="text-4xl text-gray-800 dark:text-gray-100 font-semibold mb-6">
            Volunteer Opportunities
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-8 font-extralight">
            Find meaningful volunteer positions with small and medium-sized enterprises. Help local
            businesses grow while gaining valuable experience and making a difference in your
            community.
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-sm mb-8">
          <div className="relative flex-grow">
            <SearchIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400 dark:text-gray-500" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search volunteer opportunities by title, organization, or keyword..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {loading ? (
          <p className="text-gray-500 dark:text-gray-400 px-4">Loading opportunities...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-lg">
            <p className="text-gray-500 dark:text-gray-400">
              {opportunities.length === 0
                ? "No volunteer opportunities are available right now. Check back soon."
                : "No opportunities match your search."}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map((o) => {
              const skills = Object.values(o.skills_data || {})
                .map((s) => (s && typeof s === "object" ? s.name : s))
                .filter(Boolean);
              return (
                <div
                  key={o.id}
                  className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow duration-200"
                >
                  <div className="h-3 bg-blue-500" />
                  <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} className="p-6">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100 mb-1">
                          {o.title}
                        </h3>
                        <p className="text-gray-600 dark:text-gray-400 font-medium">
                          {o.company_name}
                        </p>
                      </div>
                      <img
                        src={CompanyLogo}
                        alt="Company logo"
                        className="w-12 h-12 object-contain rounded"
                      />
                    </div>
                    <div className="space-y-2 mb-4">
                      {o.company_type && (
                        <div className="flex items-center text-gray-500 dark:text-gray-400">
                          <MapPinIcon className="h-4 w-4 mr-2" />
                          <span className="text-sm capitalize">
                            {String(o.company_type).replace(/_/g, " ")}
                          </span>
                        </div>
                      )}
                      <div className="flex items-center text-gray-500 dark:text-gray-400">
                        <CalendarIcon className="h-4 w-4 mr-2" />
                        <span className="text-sm">{timeAgo(o.created_at)}</span>
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 mb-4 line-clamp-3">
                      {o.description}
                    </p>
                    {skills.length > 0 && (
                      <div className="mb-4 flex flex-wrap gap-2">
                        {skills.slice(0, 3).map((s) => (
                          <span
                            key={s}
                            className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs font-medium"
                          >
                            {s}
                          </span>
                        ))}
                        {skills.length > 3 && (
                          <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded text-xs font-medium">
                            +{skills.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </motion.div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
